import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import OcRequestStatusBadge from '../../components/OrganoidRequest/OcRequestStatusBadge';
import OcCandidateTable from '../../components/OrganoidRequest/OcCandidateTable';
import OcTransitionBar from '../../components/OrganoidRequest/OcTransitionBar';
import OcStageNav from '../../components/OrganoidRequest/OcStageNav';
import OcHandoffNotes from '../../components/OrganoidRequest/OcHandoffNotes';
import OcSelectionRequirementsPanel from '../../components/OrganoidRequest/OcSelectionRequirementsPanel';
import OcWorkspaceShell from '../../components/OrganoidRequest/OcWorkspaceShell';
import {
  OC_CANDIDATE_MATCH_TYPES,
  OC_DEFAULT_CANDIDATE_MATCH_TYPE,
  buildFreeTextCandidatePayload,
  getPrimaryTransitionForPage,
  getWorkflowHandoffNotes,
} from '../../utils/ocRequest';
import { useOcRequestWorkspace } from '../../utils/useOcRequestWorkspace';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo, logError } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'OcRequestDataReviewPage';

/**
 * Data-review stage: build a local free-text OC list (no catalog), then one click
 * persists drafts and sends the request to technical review.
 */
const OcRequestDataReviewPage = () => {
  const { api, requestId, basePath, mode, readOnly } = useOcRequestWorkspace();
  const [request, setRequest] = useState(null);
  const [savedCandidates, setSavedCandidates] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [freeText, setFreeText] = useState('');
  const [matchType, setMatchType] = useState(OC_DEFAULT_CANDIDATE_MATCH_TYPE);
  const [notes, setNotes] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    const [detailRes, candRes] = await Promise.all([
      api.getDetail(),
      api.getCandidates(),
    ]);
    setLoading(false);
    if (detailRes.success) setRequest(detailRes.data);
    if (candRes.success) setSavedCandidates(candRes.data || []);
    logInfo(LOG_CONTEXT, 'data review loaded', {
      requestId,
      mode,
      savedCount: candRes.success ? (candRes.data || []).length : null,
      handoffNoteCount: getWorkflowHandoffNotes(
        detailRes.success ? detailRes.data?.workflow_actions : []
      ).length,
    });
  }, [api, requestId, mode]);

  useEffect(() => {
    load();
  }, [load]);

  const displayCandidates = useMemo(
    () => [
      ...savedCandidates,
      ...drafts.map(d => ({
        id: d.localId,
        free_text_oc_id: d.free_text_oc_id,
        display_oc_id: d.free_text_oc_id,
        match_type: d.match_type,
        review_notes: d.review_notes,
        isDraft: true,
      })),
    ],
    [savedCandidates, drafts]
  );

  const addToList = () => {
    const ocId = freeText.trim();
    if (!ocId) {
      showToast('Enter an OC id', 'warning', 'top-right', 3000);
      return;
    }
    const duplicate =
      drafts.some(d => d.free_text_oc_id.toLowerCase() === ocId.toLowerCase()) ||
      savedCandidates.some(
        c =>
          String(c.free_text_oc_id || c.display_oc_id || '')
            .toLowerCase() === ocId.toLowerCase()
      );
    if (duplicate) {
      showToast('That OC id is already on the list', 'warning', 'top-right', 3000);
      return;
    }
    const entry = {
      localId: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      free_text_oc_id: ocId,
      match_type: matchType || OC_DEFAULT_CANDIDATE_MATCH_TYPE,
      review_notes: notes,
    };
    logInfo(LOG_CONTEXT, 'draft candidate added locally', {
      requestId,
      free_text_oc_id: ocId,
      match_type: entry.match_type,
      hasReviewNotes: Boolean(entry.review_notes),
    });
    setDrafts(prev => [...prev, entry]);
    setFreeText('');
    setNotes('');
    setMatchType(OC_DEFAULT_CANDIDATE_MATCH_TYPE);
  };

  const removeRow = async rowId => {
    if (String(rowId).startsWith('draft-')) {
      setDrafts(prev => prev.filter(d => d.localId !== rowId));
      logInfo(LOG_CONTEXT, 'draft candidate removed locally', { requestId, rowId });
      return;
    }
    setDeletingId(rowId);
    const result = await api.deleteCandidate(rowId);
    setDeletingId(null);
    if (result.success) {
      showToast('Candidate removed', 'success', 'top-right', 2000);
      setSavedCandidates(prev => prev.filter(c => c.id !== rowId));
    } else {
      showToast(result.messages?.[0] || 'Delete failed', 'error', 'top-right', 4000);
    }
  };

  /**
   * Single-click path: persist unsaved drafts, attach them on the transition payload,
   * then let OcTransitionBar call send_to_technical_review.
   */
  const beforeSendToTechnicalReview = async action => {
    if (action !== 'send_to_technical_review') return true;

    const draftSnapshot = [...drafts];
    const candidatePayloads = draftSnapshot.map(buildFreeTextCandidatePayload);
    const totalAfter =
      savedCandidates.length + candidatePayloads.length;

    if (totalAfter < 1) {
      showToast(
        'Add at least one candidate before sending to technical review',
        'warning',
        'top-right',
        4000
      );
      return false;
    }

    if (draftSnapshot.length) {
      logInfo(LOG_CONTEXT, 'persisting drafts before send_to_technical_review', {
        requestId,
        count: draftSnapshot.length,
      });
      const remaining = [];
      for (const draft of draftSnapshot) {
        const payload = buildFreeTextCandidatePayload(draft);
        const result = await api.createCandidate(payload);
        if (!result.success) {
          remaining.push(draft);
          logError(LOG_CONTEXT, 'createCandidate failed before transition', {
            free_text_oc_id: draft.free_text_oc_id,
            messages: result.messages,
          });
        }
      }
      if (remaining.length) {
        setDrafts(remaining);
        showToast(
          `Could not save ${remaining.length} candidate(s) — fix and retry`,
          'error',
          'top-right',
          5000
        );
        return false;
      }
      setDrafts([]);
      const refresh = await api.getCandidates();
      if (refresh.success) setSavedCandidates(refresh.data || []);
    }

    return {
      extraPayload: {
        // Backend may create from this list and/or rely on POSTed rows (require_candidates).
        candidates: candidatePayloads,
      },
    };
  };

  if (loading || !request || !api) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  const primary = getPrimaryTransitionForPage('data-review', request.status);
  const body = (
    <>
      <Button as={Link} to={basePath} variant="link" className="px-0 mb-2">
        ← Back to detail
      </Button>
      <div className="d-flex align-items-center gap-2 mb-3">
        <h2 className="mb-0">Data review — {request.request_number}</h2>
        <OcRequestStatusBadge status={request.status} />
      </div>
      <OcStageNav basePath={basePath} active="data-review" />

      <OcSelectionRequirementsPanel request={request} />

      <OcHandoffNotes actions={request.workflow_actions} />

      <div className="oc-glass-panel mb-3">
        <div className="oc-glass-body">
      <p className="text-muted small mb-2">
        Add OC ids to the list, then send to technical review in one step. Candidates are
        free-text only (not linked to the culture catalog).
      </p>

      {!readOnly ? (
      <Row className="g-2 mb-3 align-items-end">
        <Col md={3}>
          <Form.Label>OC id</Form.Label>
          <Form.Control
            value={freeText}
            onChange={e => setFreeText(e.target.value)}
            placeholder="e.g. CRC-001"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addToList();
              }
            }}
          />
        </Col>
        <Col md={3}>
          <Form.Label>Match type</Form.Label>
          <Form.Select value={matchType} onChange={e => setMatchType(e.target.value)}>
            {OC_CANDIDATE_MATCH_TYPES.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label>Review notes</Form.Label>
          <Form.Control value={notes} onChange={e => setNotes(e.target.value)} />
          <Form.Text className="text-muted">
            Lab only — not shown on the client portal.
          </Form.Text>
        </Col>
        <Col md="auto">
          <Button variant="outline-primary" onClick={addToList}>
            Add to list
          </Button>
        </Col>
      </Row>
      ) : null}

      {drafts.length ? (
        <Alert variant="info" className="py-2 mb-0">
          {drafts.length} unsaved candidate(s) will be saved when you send to technical
          review.
        </Alert>
      ) : null}
        </div>
      </div>

      <OcCandidateTable
        candidates={displayCandidates}
        mode="internal"
        onDelete={readOnly ? undefined : removeRow}
        deletingId={deletingId}
      />

      {!readOnly ? (
      <div className="mt-3">
        <OcTransitionBar
          api={api}
          requestId={requestId}
          request={request}
          action={primary?.action || 'send_to_technical_review'}
          label={primary?.label || 'Send to technical review'}
          beforeTransition={beforeSendToTechnicalReview}
          secondaryActions={[
            {
              action: 'mark_no_matches',
              label: 'Mark no matches',
              variant: 'outline-warning',
            },
          ]}
          onSuccess={data => {
            setRequest(data);
            setDrafts([]);
            api.getCandidates().then(candRes => {
              if (candRes.success) setSavedCandidates(candRes.data || []);
            });
          }}
        />
      </div>
      ) : null}
    </>
  );

  if (mode === 'labPublic') return body;
  return <OcWorkspaceShell wide theme="yellow">{body}</OcWorkspaceShell>;
};

export default OcRequestDataReviewPage;
